/**
 * Travle «Пазл» helpers — pure. Guesses attach to ANY reached country, so the
 * placed pieces form a network, not a line; the route line on a win is the
 * shortest chain through that network from A to B.
 */

export type Box = [number, number, number, number];

/** BFS over `nodes` only (A, placed pieces, B) using land borders. */
export function playerRoute(
  from: string,
  to: string,
  placed: readonly string[],
  borders: (code: string) => readonly string[],
): string[] | null {
  const nodes = new Set([from, to, ...placed]);
  const prev = new Map<string, string | null>([[from, null]]);
  const queue = [from];
  while (queue.length > 0) {
    const cur = queue.shift() as string;
    if (cur === to) {
      const out = [to];
      let p = prev.get(to) ?? null;
      while (p !== null) {
        out.unshift(p);
        p = prev.get(p) ?? null;
      }
      return out;
    }
    for (const n of borders(cur)) {
      if (!nodes.has(n) || prev.has(n)) continue;
      prev.set(n, cur);
      queue.push(n);
    }
  }
  return null;
}

/**
 * The camera: union of the boxes, padded by `pad` (fraction of the larger side),
 * never smaller than `minSize` on either axis, centred.
 */
export function cameraBox(boxes: readonly Box[], pad: number, minSize: number): Box | null {
  if (boxes.length === 0) return null;
  let [x0, y0, x1, y1] = [Infinity, Infinity, -Infinity, -Infinity];
  for (const [a, b, c, d] of boxes) {
    x0 = Math.min(x0, a);
    y0 = Math.min(y0, b);
    x1 = Math.max(x1, c);
    y1 = Math.max(y1, d);
  }
  const p = Math.max(x1 - x0, y1 - y0) * pad;
  x0 -= p;
  y0 -= p;
  x1 += p;
  y1 += p;
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const w = Math.max(minSize, x1 - x0);
  const h = Math.max(minSize, y1 - y0);
  return [cx - w / 2, cy - h / 2, cx + w / 2, cy + h / 2];
}

/** Scale + translate that fits `box` into a view of `vw`×`vh`, centred. */
export function fitTransform(box: Box, vw: number, vh: number): { k: number; x: number; y: number } {
  const [x0, y0, x1, y1] = box;
  const k = Math.min(vw / (x1 - x0), vh / (y1 - y0));
  return { k, x: vw / 2 - ((x0 + x1) / 2) * k, y: vh / 2 - ((y0 + y1) / 2) * k };
}
